import { useTrackView } from "@/hooks/views/useViewTracking";
import { ItemType, Note, TranscriptEnum } from "@knowt/syncing/graphql/schema";
import { useBookmark } from "@knowt/syncing/hooks/bookmarks/useBookmarks";
import { useNoteFlashcardSet } from "@knowt/syncing/hooks/flashcards/useFlashcards";
import { useAllFolders } from "@knowt/syncing/hooks/folders/useFolders";
import { useNote, useNotebook } from "@knowt/syncing/hooks/notes/useNotes";
import { useCurrentUser } from "@knowt/syncing/hooks/user/useCurrentUser";
import { fromEntries } from "@knowt/syncing/utils/genericUtils";
import { useSidebarContextSelector } from "@/components/NotePage/hooks/SidebarContext";
import styles from "@/components/toPrint/NoteToPrint/noteToPrint.module.css";
import { useUpgradePopupContextSelector } from "@/features/Payments/PaywallPopup/UpgradePopupContext";
import { useContentItemsSharingPopupContextSelector } from "@/features/ShareSettings/hooks/useContentItemsSharingContext";
import { isVideo } from "@knowt/syncing/hooks/media/checkers";
import { useMedia } from "@knowt/syncing/hooks/media/useMedia";
import useCombinedState from "@knowt/syncing/utils/hooks/useCombinedState";
import { createContext, useContextSelector } from "@knowt/syncing/utils/use-context-selector";
import { useParams, useSearchParams } from "next/navigation";
import { useRouter } from "@bprogress/next";
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import useDebounce from "react-use/lib/useDebounce";
import { NotePageContext as NotePageContextProps, OwnerData } from "../types";
import { useClasses } from "@knowt/syncing/hooks/classes/useClass";
import { omit } from "lodash-es";

const NotePageContext = createContext<NotePageContextProps | null>(null);

type NotePageContextProviderProps = {
    isNoteOwner: boolean;

    initialNote: Note;
    ownerData?: OwnerData | null;
    children: ReactNode;
};

export const NotePageContextProvider = ({
    isNoteOwner,

    initialNote,
    ownerData,
    children,
}: NotePageContextProviderProps) => {
    const router = useRouter();
    const params = useParams();
    const searchParams = useSearchParams();

    const isNew = searchParams?.get("isNew") === "true";
    const autoCreateFlashcards = searchParams?.get("autoCreateFlashcards") === "true";

    const { isTeacher, hasReachedAIUsage } = useCurrentUser();

    const openUpgradePopup = useUpgradePopupContextSelector(state => state.openUpgradePopup);
    const openContentItemsSharingPopup = useContentItemsSharingPopupContextSelector(
        state => state.openContentItemsSharingPopup
    );

    const noteId = (
        params?.noteId && Array.isArray(params?.noteId) ? params?.noteId[0] : (params?.noteId ?? initialNote?.noteId)
    ) as string;

    const { note: _note, mutate, update, isLoading: isNoteLoading } = useNote({ noteId, fallbackData: initialNote });
    const note = _note as Note; // because we have a truthy `fallbackData`

    useEffect(() => {
        if (isNew && note && isTeacher) {
            openContentItemsSharingPopup({
                notes: note ? [note] : undefined,
                showClassCreation: true,
                itemType: ItemType.NOTE,
                isNew: true,
            });
        }
    }, [isNew]);

    const loading = isNoteLoading;
    const nonEditable = loading || !isNoteOwner || !!note.lock;

    const showingMediaForEmptyNoteDone = useRef(false);

    const isNoteChatSplitOpen = useSidebarContextSelector(s => s.isNoteChatSplitOpen);
    const toggleNoteChatSplit = useSidebarContextSelector(s => s.toggleNoteChatSplit);

    useTrackView({
        itemId: noteId,
        itemType: ItemType.NOTE,
        data: omit(note, "content"),
        canTrack: () => !!note?.title,
    });

    const { allFolders } = useAllFolders({ isEnabled: isNoteOwner });

    const [showTrashedItems, setShowTrashedItems] = useState(Boolean(note?.trash));
    const [explorerFilters, setExplorerFilters] = useCombinedState({
        folderId: null,
        tag: null,
        classId: null,
    });

    const toggleShowTrashedItems = useCallback(() => {
        setShowTrashedItems(val => !val);
    }, []);

    const explorerFolders = useMemo(() => {
        return allFolders
            ? fromEntries(Object.entries(allFolders).filter(([, folder]) => folder.trash === showTrashedItems))
            : null;
    }, [allFolders, showTrashedItems]);

    const { notes: userNotes } = useNotebook({
        showFoldersNotes: true,
        inTrash: showTrashedItems,
        isEnabled: isNoteOwner,
        showClassNotes: true,
    });

    const { classes: userClasses } = useClasses({
        isEnabled: isNoteOwner,
    });

    const saveContent = useCallback(
        async (newContent: string) => {
            await update({ content: newContent });
        },
        [update]
    );

    const {
        flashcards,
        flashcardSet,
        loading: loadingFlashcardSet,
    } = useNoteFlashcardSet({
        noteId,
        raw: !isNoteOwner,
    });

    const { media } = useMedia({ mediaId: note?.mediaId });
    const noteMedia = media?.userId === note?.userId || media?.public ? media : null;

    const { bookmarked, toggleBookmarked } = useBookmark({
        itemId: noteId,
        type: ItemType.NOTE,
        classId: note?.classId,
        itemUserId: note?.userId,
    });

    const [showCreateFlashcardsOptionsPopup, setShowCreateFlashcardsOptionsPopup] = useState(false);
    const [folderPopupOpen, setFolderPopupOpen] = useState(false);

    const [newNotePopupStates, updateNewNotePopupStates] = useCombinedState<{
        isOpen: boolean;
        noteProps?: Partial<Note>;
    }>({ isOpen: false, noteProps: {} });

    const [numberOfWordsInTheNote, setNumberOfWordsInTheNote] = useState(0);

    const noteToPrintRef = useRef(null);

    useDebounce(
        () => {
            if (note?.content !== undefined && note?.content !== null) {
                setNumberOfWordsInTheNote(note.content.trim().split(/\s+/).length);
            }
        },
        2000,
        [note?.content]
    );

    const createFlashcardSet = useCallback(async () => {
        if (isNoteOwner) {
            setShowCreateFlashcardsOptionsPopup(true);
        }
    }, [isNoteOwner]);

    const goToFlashcards = useCallback(async () => {
        router.push(`/flashcards/${flashcardSet?.flashcardSetId}`);
    }, [flashcardSet?.flashcardSetId, router]);

    const printDocument = useReactToPrint(
        useMemo(
            () => ({
                contentRef: noteToPrintRef,
                documentTitle: note?.title || "Knowt",
                bodyClass: styles.printedBodyClass,
                pageStyle: `
                    @media print {
                        @page {
                            size: A4;
                            margin: 3rem;
                            background-color: white;
                        }
                    }
                    body {
                        print-color-adjust: exact;
                        -webkit-print-color-adjust: exact;
                    }
  \`,
        `,
            }),
            [note?.title]
        )
    );

    const hasTriedToAutoCreatedFlashcards = useRef(false);

    useEffect(() => {
        if ((!note?.content || note?.content === "<p></p>") && note?.mediaId && !showingMediaForEmptyNoteDone.current) {
            showingMediaForEmptyNoteDone.current = true;
            if (!isNoteChatSplitOpen) toggleNoteChatSplit({ isMediaTabOpen: true });
        }
    }, [note?.content, note?.mediaId, isNoteChatSplitOpen, toggleNoteChatSplit]);

    useEffect(() => {
        if (
            !autoCreateFlashcards ||
            hasTriedToAutoCreatedFlashcards.current ||
            loadingFlashcardSet ||
            flashcards?.length
        ) {
            return;
        }

        hasTriedToAutoCreatedFlashcards.current = true;

        if (
            !hasReachedAIUsage(
                [TranscriptEnum.FULL, TranscriptEnum.MIN].includes(noteMedia?.transcript)
                    ? isVideo(noteMedia)
                        ? "vSets"
                        : "pSets"
                    : "nSets"
            )
        )
            router.push(`/ai-note-flashcards/${note?.noteId}`);
    }, [autoCreateFlashcards, hasReachedAIUsage, note?.noteId, openUpgradePopup, loadingFlashcardSet, flashcards]);

    // Check if we just moved / created a note to class -> open section sharing popup
    useEffect(() => {
        const addedFileId = localStorage.getItem("addedFileId");
        if (addedFileId === noteId) {
            localStorage.removeItem("addedFileId");
            openContentItemsSharingPopup({ notes: note ? [note] : undefined, itemType: ItemType.NOTE });
        }
    }, []);

    const value = useMemo(
        () => ({
            noteId,
            note,
            initialNote,
            ownerData,
            isNoteOwner,
            loading,
            nonEditable,
            bookmarked,
            showCreateFlashcardsOptionsPopup,
            folderPopupOpen,
            newNotePopupStates,
            explorerNotes: userNotes,
            explorerFolders,
            flashcards,
            noteToPrintRef,
            numberOfWordsInTheNote,
            noteMedia,
            showTrashedItems,
            explorerFilters,
            userClasses,
            mutate,
            update,
            toggleBookmarked,
            createFlashcardSet,
            goToFlashcards,
            setShowCreateFlashcardsOptionsPopup,
            setFolderPopupOpen,
            updateNewNotePopupStates,
            printDocument,
            saveContent,
            toggleShowTrashedItems,
            setExplorerFilters,
        }),
        [
            noteId,
            note,
            initialNote,
            ownerData,
            isNoteOwner,
            loading,
            nonEditable,
            bookmarked,
            showCreateFlashcardsOptionsPopup,
            folderPopupOpen,
            newNotePopupStates,
            userNotes,
            explorerFolders,
            flashcards,
            numberOfWordsInTheNote,
            noteMedia,
            showTrashedItems,
            explorerFilters,
            userNotes,
            userClasses,
            mutate,
            update,
            toggleBookmarked,
            createFlashcardSet,
            goToFlashcards,
            updateNewNotePopupStates,
            printDocument,
            saveContent,
            toggleShowTrashedItems,
            setExplorerFilters,
        ]
    );

    return <NotePageContext.Provider value={value}>{children}</NotePageContext.Provider>;
};

export const useNotePageContextSelector = <T,>(selector: (state: NotePageContextProps) => T) =>
    useContextSelector(NotePageContext, selector);
